import axios from 'axios';

const backendRoot = (import.meta.env.VITE_BACKEND_URL ?? '').replace(/\/$/, '');

const axiosClient = axios.create({
  baseURL: backendRoot ? `${backendRoot}/api/v1` : '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

const AUTH_INTERCEPTOR_EXCLUSIONS = ['/user/login', '/auth/refresh-token', '/auth/forgot-password', '/auth/reset-password'];

let opsTenantId = null;

export function setOpsTenantId(id) {
  opsTenantId = id || null;
}

axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (opsTenantId) {
    config.headers['X-Tenant-Id'] = opsTenantId;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url ?? '';

    if (AUTH_INTERCEPTOR_EXCLUSIONS.some((route) => requestUrl.includes(route))) {
      return Promise.reject(error);
    }

    if (error.response?.data?.code === 'INSUFFICIENT_CREDITS') {
      window.dispatchEvent(new CustomEvent('insufficient-credits'));
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axiosClient.post('/auth/refresh-token');
        const newToken = data.tokens.accessToken;
        localStorage.setItem('accessToken', newToken);
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('accessToken');
        window.location.replace('/login');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
